import { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Button, Table, Tag, Tooltip, App } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { BroadcastListItem, BroadcastStatus } from '@mygate/common';
import { broadcastsService } from '@/services/broadcasts';
import { ApiError } from '@/services/api';
import { formatDateTime } from '@/utils/date';
import { BroadcastWizard } from '@/components/broadcast/BroadcastWizard';
import { BroadcastDetailDrawer } from '@/components/broadcast/BroadcastDetailDrawer';
import { BROADCAST_STATUS_COLORS } from '@/components/broadcast/constants';

const POLL_INTERVAL_MS = 5000;

export function BroadcastsPage() {
  const { message } = App.useApp();

  const [broadcasts, setBroadcasts] = useState<BroadcastListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const initializedRef = useRef(false);
  const paginationRef = useRef(pagination);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBroadcasts = useCallback(
    async (page = pagination.page, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await broadcastsService.list(page, limit);
        if (response.success && response.data) {
          setBroadcasts(response.data.broadcasts);
          setPagination(prev => ({ ...prev, total: response.data?.total || 0 }));
        }
      } catch (error) {
        message.error(error instanceof ApiError ? error.message : 'Failed to load broadcasts');
      } finally {
        setLoading(false);
      }
    },
    [message],
  );

  // Initial load
  useEffect(() => {
    initializedRef.current = true;
    fetchBroadcasts(1, pagination.limit);
  }, []);

  // Refetch when pagination changes (after the initial load)
  useEffect(() => {
    if (initializedRef.current) {
      fetchBroadcasts(pagination.page, pagination.limit);
    }
  }, [pagination.page, pagination.limit]);

  // Poll while any broadcast is still SENDING; stop as soon as none are.
  useEffect(() => {
    const hasSending = broadcasts.some(b => b.status === BroadcastStatus.SENDING);

    if (hasSending && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => {
        fetchBroadcasts(paginationRef.current.page, paginationRef.current.limit);
      }, POLL_INTERVAL_MS);
    }

    if (!hasSending && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [broadcasts]);

  const handleRowClick = (record: BroadcastListItem) => {
    setSelectedId(record.id);
    setDrawerOpen(true);
  };

  const handleWizardSent = () => {
    setWizardOpen(false);
    if (pagination.page === 1) {
      fetchBroadcasts(1, pagination.limit);
    } else {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const columns: ColumnsType<BroadcastListItem> = [
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: date => formatDateTime(date),
    },
    {
      title: 'Title (DE)',
      dataIndex: 'title',
      key: 'title',
      width: 260,
      render: (title: string, record) => (
        <Button
          type="link"
          className="table-cell-link"
          onClick={event => {
            event.stopPropagation();
            handleRowClick(record);
          }}
        >
          {title}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: BroadcastStatus) => (
        <Tag title={status} color={BROADCAST_STATUS_COLORS[status] || 'default'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Recipients',
      key: 'recipients',
      align: 'right',
      width: 110,
      render: (_, record) => record.audience.total,
    },
    {
      title: <Tooltip title="Devices Expo accepted the push for; 0 while the broadcast is still queued">Sent</Tooltip>,
      key: 'sent',
      align: 'right',
      width: 90,
      render: (_, record) =>
        record.stats.statsAvailable ? (
          record.stats.sent
        ) : (
          <Tooltip title="Delivery stats expire after 30 days">—</Tooltip>
        ),
    },
    {
      title: (
        <Tooltip title="Of the sent devices, those Apple or Google accepted the push for, checked about 15 minutes after sending. Whether the device displayed it is not tracked">
          Delivered
        </Tooltip>
      ),
      key: 'delivered',
      align: 'right',
      width: 90,
      render: (_, record) =>
        record.stats.statsAvailable ? (
          record.stats.delivered
        ) : (
          <Tooltip title="Delivery stats expire after 30 days">—</Tooltip>
        ),
    },
    {
      title: (
        <Tooltip title="Devices the push did not reach: refused by Expo, or sent and then refused by Apple or Google, so a device can count in Sent and here. A device reported as gone gets no further pushes until the app registers again">
          Failed
        </Tooltip>
      ),
      key: 'failed',
      align: 'right',
      width: 90,
      render: (_, record) =>
        record.stats.statsAvailable ? (
          record.stats.failed
        ) : (
          <Tooltip title="Delivery stats expire after 30 days">—</Tooltip>
        ),
    },
    {
      title: 'Sent by',
      dataIndex: 'sentBy',
      key: 'sentBy',
      width: 160,
      ellipsis: true,
    },
  ];

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Kunden gezielt mit Nachrichten erreichen.</Typography.Text>

        <Button type="primary" icon={<PlusOutlined />} onClick={() => setWizardOpen(true)}>
          New broadcast
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={broadcasts}
        rowKey="id"
        loading={loading}
        onRow={record => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          onChange: (page, pageSize) => setPagination(prev => ({ ...prev, page, limit: pageSize })),
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} broadcasts`,
        }}
        tableLayout="fixed"
        scroll={{ x: 1100 }}
      />

      <BroadcastWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onSent={handleWizardSent} />

      <BroadcastDetailDrawer
        broadcastId={selectedId}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedId(null);
        }}
      />
    </>
  );
}
